import { ButtonWithIcon } from '@/components/IconButton';
import ReactIcon, { default as Icon } from '@/components/ReactIcon';
import { ScheduleDTO, ShopDTO } from '@/DTO/shopResponseDTO';
import {
    CartServiceType,
    CartValuesServiceStore,
} from '@/store/shoppingCart/shoppingCartStore';
import React, { useMemo } from 'react';
import { LuCalendar } from 'react-icons/lu';
import { MdAddShoppingCart, MdFlashOn } from 'react-icons/md';
import {
    BadgeHour,
    ButtonShop,
    CardShopWrapper,
    DetailsShopWrapper,
    HoursElementsText,
    VideoScreen,
    WrapHourDescription,
} from './styles';
import { FirstHeatSvg } from '@/components/FirstHeatToggle';
import { GiftDTO } from '@/DTO/authDTO';
import { useGiftsStore } from '@/hooks/useGiftsStore';
import { useUserProfile } from '@/hooks/useUserData';
import { ReservationDayEligibilityStatus } from '@/store/reservation/reservationStore';
import { WaveLevelEnum } from '@/helpers/utils';

type CardShopProps = {
    itemShop: ShopDTO;
    date: Date;
    schedule: ScheduleDTO;
    handleAddToCart: (cartCurrentValue: CartValuesServiceStore, accessProfile: string) => void;
    isUndecidedDate?: boolean;
    isFirstHeat: boolean;
    currentGift?: GiftDTO;
    reservationDayStatus?: ReservationDayEligibilityStatus;
};

export const CardSchedule: React.FC<CardShopProps> = ({
    itemShop: {
        id,
        exhibitionName,
        waveSide,
        waveLevel,
        hottestSchedule,
        image,
        value,
    },
    date,
    schedule,
    handleAddToCart,
    isUndecidedDate,
    isFirstHeat,
    reservationDayStatus,
}) => {
    const giftsStore = useGiftsStore();
    const isAvailable = schedule.available > 0;
    const isHottestSchedule = useMemo(
        () => hottestSchedule?.id === schedule.id,
        [hottestSchedule?.id, schedule.id]
    );
    const user = useUserProfile();

    const isFreeSessionAvailable = useMemo(() => {
        if (!reservationDayStatus) return false;
        if (waveLevel === WaveLevelEnum.REEF) return reservationDayStatus.freeReefStatus === 'available';
        if (waveLevel === WaveLevelEnum.BAY) return reservationDayStatus.freeBayStatus === 'available';
        return false;
    }, [reservationDayStatus, waveLevel]);

    if(!isAvailable) return null

    const onClick = (giftData?: { isFirstHeat: boolean; giftId: number }) => {
        handleAddToCart({
            id: String(id),
            title: exhibitionName,
            value: isFirstHeat ? 0 : isFreeSessionAvailable ? 0 : value,
            date,
            type: CartServiceType.Waves,
            scheduleId: schedule.id,
            scheduleHour: schedule.name,
            waveLevel,
            waveSide,
            available: schedule.available,
            freeSession: !isFirstHeat && isFreeSessionAvailable ? true : undefined,
            ...giftData
        }, user.data?.accessProfile.name || "Guest");
    };

    const handleAddGift = () => {
        giftsStore.update({
            isVisible: true,
            callback: gift => {
                if (gift) {
                    onClick({ giftId: gift.id, isFirstHeat: true });
                }
            },
        });
    };

    return (
        <CardShopWrapper>
            <VideoScreen src={image} alt={exhibitionName} />
            <DetailsShopWrapper>
                <WrapHourDescription>
                    <h3>{exhibitionName}</h3>
                    <BadgeHour $isCurrent={isHottestSchedule}>
                        {isHottestSchedule && (
                            <Icon Icon={MdFlashOn} size={'1rem'} />
                        )}
                        {schedule.name}
                    </BadgeHour>
                </WrapHourDescription>

                {isUndecidedDate && (
                    <section>
                        <ReactIcon
                            Icon={LuCalendar}
                            size={'1rem'}
                            color="#71717A"
                        />
                        <HoursElementsText>
                            Data ainda não definida
                        </HoursElementsText>
                    </section>
                )}
            </DetailsShopWrapper>

            {!isFirstHeat && !isFreeSessionAvailable && (
                <ButtonShop onClick={() => onClick()}>
                    <Icon Icon={MdAddShoppingCart} size={'1.25rem'} />
                    R$ {value}
                </ButtonShop>
            )}
            {!isFirstHeat && isFreeSessionAvailable && (
                <ButtonWithIcon variant="blue" onClick={() => onClick()}>
                    <LuCalendar size={'1.25rem'} />
                    Resgatar
                </ButtonWithIcon>
            )}
            {isFirstHeat && (
                <ButtonWithIcon variant="blue" onClick={handleAddGift}>
                    <FirstHeatSvg />
                    Resgatar
                </ButtonWithIcon>
            )}
        </CardShopWrapper>
    );
};
